/* Türen und Verbindungen: berechnet den offenen Zustand von func_door, func_button und drehenden Türen
 * sowie die Linien zwischen target und targetname. Reine Logik ohne DOM. */
import type { BBox, Doc, Entity, Sel, Vec3 } from './types.ts';
import Core from '../core.ts';
import I18n from '../i18n.ts';
export interface SlideInfo { kind: 'slide'; cls: string; bb: BBox; c0: Vec3[]; c1: Vec3[]; vec: Vec3; travel: number; label: string }
export interface RotInfo { kind: 'rot'; cls: string; bb: BBox; c0: Vec3[]; c1: Vec3[]; pivot: Vec3; axis: number; deg: number; hasOrigin: boolean; label: string }
/** Öffnungsweg einer Tür: Eckpunkte der Hülle geschlossen (c0) und offen (c1) */
export type DoorInfo = SlideInfo | RotInfo;
export interface Link { from: Entity; to: Entity; a: Vec3; b: Vec3; kill: boolean }

const Doors = (function (Core) {
  'use strict';
  const _ = I18n.t;
  const SLIDE = /^(func_door|func_button)$/, ROT = /^(func_door_rotating|func_rot_button)$/;
  const corners = (bb: BBox): Vec3[] => { const r: Vec3[] = []; for (let i = 0; i < 8; i++) r.push([i & 1 ? bb.max[0] : bb.min[0], i & 2 ? bb.max[1] : bb.min[1], i & 4 ? bb.max[2] : bb.min[2]]); return r; };
  const EDGES: [number, number][] = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [1, 3], [4, 6], [5, 7], [0, 4], [1, 5], [2, 6], [3, 7]];
  const AXN = ['X', 'Y', 'Z'];
  const r1 = (x: number): number => Math.round(x * 100) / 100;

  /** Richtung aus dem angle-Feld: -1 hoch, -2 runter, sonst Gierwinkel in Grad. */
  function moveDir(kv: Record<string, string>): { v: Vec3; name: string } {
    let a = kv.angle !== undefined && kv.angle !== '' ? Number(kv.angle) : (kv.angles ? Number(String(kv.angles).trim().split(/\s+/)[1]) : 0);
    if (isNaN(a)) a = 0;
    if (a === -1) return { v: [0, 0, 1], name: _('doors.up') };
    if (a === -2) return { v: [0, 0, -1], name: _('doors.down') };
    const r = a * Math.PI / 180, v: Vec3 = [Math.cos(r), Math.sin(r), 0];
    v[0] = Math.abs(v[0]) < 1e-9 ? 0 : v[0]; v[1] = Math.abs(v[1]) < 1e-9 ? 0 : v[1];
    return { v: v, name: a + '° (' + (v[0] > 0.5 ? '+X' : v[0] < -0.5 ? '−X' : v[1] > 0.5 ? '+Y' : v[1] < -0.5 ? '−Y' : _('ta.slanted')) + ')' };
  }

  /** Beschreibt den Öffnungsweg einer Tür. Liefert null für andere Entities. */
  const memo = new WeakMap<object, DoorInfo | null>();
  /** Wie describeRaw, aber je Entity-Objekt nur einmal berechnet (Entities werden bei Änderungen ersetzt, nie verändert). */
  function describe(e: Entity): DoorInfo | null {
    if (!e || typeof e !== 'object') return describeRaw(e);
    let r = memo.get(e);
    if (r === undefined) { r = describeRaw(e); memo.set(e, r); }
    return r;
  }
  function describeRaw(e: Entity): DoorInfo | null {
    const cls = e.kv.classname || '';
    if (!e.brushes || !e.brushes.length || !(SLIDE.test(cls) || ROT.test(cls))) return null; // erst die Klasse prüfen: die Hülle der ganzen Welt kostet bei großen Maps
    const bb = Core.bboxOf(e.brushes), c0 = corners(bb);
    if (SLIDE.test(cls)) {
      const d = moveDir(e.kv), lip = Number(e.kv.lip) || 0, size = [0, 1, 2].map((i) => bb.max[i] - bb.min[i]) as Vec3;
      const travel = Math.max(0, Math.abs(d.v[0] * size[0]) + Math.abs(d.v[1] * size[1]) + Math.abs(d.v[2] * size[2]) - lip);
      const vec = d.v.map((x) => x * travel) as Vec3;
      return {
        kind: 'slide', cls: cls, bb: bb, c0: c0, c1: c0.map((p): Vec3 => [p[0] + vec[0], p[1] + vec[1], p[2] + vec[2]]), vec: vec, travel: travel,
        label: cls.replace('func_', '') + ': ' + d.name + ', ' + _('doors.travel', { n: r1(travel) }) + (lip ? ' (Lip ' + lip + ')' : '')
      };
    }
    if (ROT.test(cls)) {
      const sf = Number(e.kv.spawnflags) || 0, axis = sf & 64 ? 0 : sf & 128 ? 1 : 2;
      let deg = Number(e.kv.distance); if (isNaN(deg) || e.kv.distance === undefined || e.kv.distance === '') deg = 90;
      if (sf & 2) deg = -deg;
      const o = Core.parseVec(e.kv.origin), pivot: Vec3 = o || ([0, 1, 2].map((i) => (bb.min[i] + bb.max[i]) / 2) as Vec3);
      const M = Core.rotMat(axis, deg);
      const c1 = c0.map((p): Vec3 => { const q = Core.sub(p, pivot); return Core.add([Core.dot(M[0], q), Core.dot(M[1], q), Core.dot(M[2], q)], pivot); });
      return {
        kind: 'rot', cls: cls, bb: bb, c0: c0, c1: c1, pivot: pivot, axis: axis, deg: deg, hasOrigin: !!o,
        label: cls.replace('func_', '') + ': ' + _('doors.rot', { deg: r1(Math.abs(deg)), axis: AXN[axis] }) + (deg < 0 ? ' ' + _('doors.reversed') : '') + (o ? '' : ', ' + _('doors.pivot_center'))
      };
    }
    return null;
  }

  const centerOf = (e: Entity): Vec3 | null => {
    if (e.brushes && e.brushes.length) { const bb = Core.bboxOf(e.brushes); return [0, 1, 2].map((i) => (bb.min[i] + bb.max[i]) / 2) as Vec3; }
    return Core.parseVec(e.kv.origin);
  };
  /** Linien von jedem Entity mit target oder killtarget zu allen passenden targetname. */
  function links(doc: Doc): Link[] {
    const byName = new Map<string, Entity[]>();
    doc.ents.forEach((e) => { const n = e.kv.targetname; if (n) { if (!byName.has(n)) byName.set(n, []); (byName.get(n) as Entity[]).push(e); } });
    const out: Link[] = [];
    doc.ents.forEach((e) => {
      ['target', 'killtarget'].forEach((k) => {
        const t = e.kv[k]; if (!t) return;
        const a = centerOf(e);
        (byName.get(t) || []).forEach((o) => { const b = centerOf(o); if (a && b && o !== e) out.push({ from: e, to: o, a: a, b: b, kill: k === 'killtarget' }); });
      });
    });
    return out;
  }
  const active = (e: Entity, sel: Sel, all?: boolean): boolean => all || sel.e.has(e.id) || (e.brushes && e.brushes.some((b) => sel.b.has(b.id)));
  return { active: active, describe: describe, links: links, corners: corners, EDGES: EDGES, centerOf: centerOf };
})(Core);
export default Doors;
